import type {
  CompanionFieldKey,
  CompanionFieldsConfigByRole,
  CompanionFormData,
} from "@/lib/companionFields";
import {
  COMPANION_FIELD_KEYS,
  COMPANION_FIELD_LABELS,
  companionFieldConfigForRole,
  companionSlotLabel,
  getCompanionFieldLabels,
} from "@/lib/companionFields";
import type { BookingMessages } from "@/i18n/types";
import {
  getMissingPrimaryGuestFields,
  type PrimaryGuestFieldsConfig,
  type PrimaryGuestFormData,
} from "@/lib/primaryGuestFields";

export type GuestAddressField = "street" | "postal_code" | "city";

export const GUEST_ADDRESS_LABELS: Record<GuestAddressField, string> = {
  street: "Straße",
  postal_code: "PLZ",
  city: "Ort",
};

export type GuestSummaryLine = {
  label: string;
  value: string;
};

export function guestSummaryLines(input: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  postalCode: string;
  city: string;
  licensePlate: string;
}): GuestSummaryLine[] {
  const address =
    [input.street.trim(), [input.postalCode.trim(), input.city.trim()].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", ") || "–";
  return [
    {
      label: "Name",
      value: [input.firstName.trim(), input.lastName.trim()].filter(Boolean).join(" ") || "–",
    },
    { label: "E-Mail", value: input.email.trim() || "–" },
    { label: "Telefon", value: input.phone.trim() || "–" },
    { label: "Adresse", value: address },
    { label: "Kennzeichen", value: input.licensePlate.trim() || "–" },
  ];
}

export function collectUnfilledMandatoryFields(
  primaryGuest: PrimaryGuestFormData,
  primaryConfig: PrimaryGuestFieldsConfig,
  companions: CompanionFormData[],
  configs: CompanionFieldsConfigByRole,
  messages?: BookingMessages,
): string[] {
  const fieldLabels = messages ? getCompanionFieldLabels(messages) : COMPANION_FIELD_LABELS;
  const items: string[] = [];
  for (const key of getMissingPrimaryGuestFields(primaryGuest, primaryConfig)) {
    items.push(
      messages
        ? messages.flow.yourDataField(fieldLabels[key])
        : `Ihre Daten: ${COMPANION_FIELD_LABELS[key]}`,
    );
  }
  let adultIndex = 0;
  let childIndex = 0;
  for (const companion of companions) {
    const slotIndex = companion.role === "child" ? childIndex++ : adultIndex++;
    const slotLabel = companionSlotLabel(companion.role, slotIndex, messages);
    const config = companionFieldConfigForRole(configs, companion.role);
    for (const key of COMPANION_FIELD_KEYS) {
      if (config[key] !== "required") continue;
      if (!companion[key].trim()) {
        items.push(`${slotLabel}: ${fieldLabels[key]}`);
      }
    }
  }
  return items;
}

export function companionSummaryLines(
  companion: CompanionFormData,
  configs: CompanionFieldsConfigByRole,
  messages?: BookingMessages,
): { key: CompanionFieldKey; label: string; value: string }[] {
  const fieldLabels = messages ? getCompanionFieldLabels(messages) : COMPANION_FIELD_LABELS;
  const config = companionFieldConfigForRole(configs, companion.role);
  const lines: { key: CompanionFieldKey; label: string; value: string }[] = [];
  for (const key of COMPANION_FIELD_KEYS) {
    if (config[key] === "off") continue;
    const value = companion[key].trim();
    if (!value) continue;
    lines.push({ key, label: fieldLabels[key], value });
  }
  return lines;
}

export function formatCompanionFieldValue(key: CompanionFieldKey, value: string): string {
  if (key === "date_of_birth" && value) {
    const [y, m, d] = value.split("-");
    if (y && m && d) return `${d}.${m}.${y}`;
  }
  return value;
}
